import { useState } from "react";
import { useAuth } from "../auth";
import { BookmarkRuler, useFlash } from "../bookmark";

// Demo on a public-domain book (Pride and Prejudice, 61 chapters); facts in our own words.
// Static: it only shows the idea, no API and no gate involved.
const LAST = 61;
const PEOPLE: { name: string; from: number; note: string }[] = [
  { name: "Elizabeth Bennet", from: 1, note: "tweede dochter, scherp en eigenwijs" },
  { name: "Mr. Bingley", from: 1, note: "nieuwe huurder van Netherfield" },
  { name: "Jane Bennet", from: 3, note: "oudste dochter" },
  { name: "Mr. Darcy", from: 3, note: "Bingley's trotse vriend" },
  { name: "Charlotte Lucas", from: 5, note: "Elizabeths vriendin" },
  { name: "Mr. Collins", from: 13, note: "neef en erfgenaam van Longbourn" },
  { name: "Mr. Wickham", from: 15, note: "charmante officier" },
  { name: "Lady Catherine", from: 29, note: "Collins' beschermvrouwe" },
];
const EVENTS: { ch: number; text: string }[] = [
  { ch: 1, text: "Mrs. Bennet hoopt dat de nieuwe buurman met een van haar dochters trouwt." },
  { ch: 3, text: "Op het bal in Meryton vindt Darcy Elizabeth niet mooi genoeg om mee te dansen." },
  { ch: 16, text: "Wickham vertelt Elizabeth hoe Darcy hem onrecht zou hebben gedaan." },
  { ch: 19, text: "Collins vraagt Elizabeth ten huwelijk; zij weigert." },
  { ch: 22, text: "Charlotte neemt het aanzoek van Collins wel aan." },
  { ch: 34, text: "Darcy doet Elizabeth een aanzoek; ze wijst hem af." },
  { ch: 35, text: "Een brief van Darcy zet Wickhams verhaal in een ander licht." },
  { ch: 46, text: "Lydia is er met Wickham vandoor." },
  { ch: 58, text: "Elizabeth en Darcy spreken zich eindelijk uit." },
];

function Demo() {
  const [bookmark, setBookmark] = useState(14);
  const [gain, flash] = useFlash();
  const known = PEOPLE.filter((p) => p.from <= bookmark);
  const hidden = PEOPLE.length - known.length;
  const events = EVENTS.filter((e) => e.ch <= bookmark).slice(-3).reverse();
  const pct = (n: number) => `${(n / LAST) * 100}%`;

  function move(n: number) {
    const diff = PEOPLE.filter((p) => p.from <= n).length - known.length;
    flash(diff > 0 ? `+${diff} ${diff === 1 ? "naam" : "namen"}` : null);
    setBookmark(n);
  }

  return (
    <section className="demo" aria-label="Voorbeeld: Pride and Prejudice">
      <div className="demo-head">
        <span className="demo-book">Pride and Prejudice</span>
        <span className="muted small">Jane Austen · voorbeeld, sleep de boekenlegger</span>
      </div>
      <BookmarkRuler first={1} last={LAST} value={bookmark} onChange={move} gain={gain} />
      <ul className="demo-threads">
        {known.map((p) => (
          <li key={p.name}>
            <span className="demo-name">
              {p.name} <small>{p.note}</small>
            </span>
            <span className="demo-track">
              <i style={{ left: pct(p.from - 1), width: pct(bookmark - p.from + 1) }} />
            </span>
          </li>
        ))}
        {hidden > 0 && (
          <li className="demo-hidden">
            nog {hidden} {hidden === 1 ? "personage" : "personages"} verborgen — die ken je nog niet
          </li>
        )}
      </ul>
      <div className="demo-events">
        <span className="demo-label">Laatst gebeurd</span>
        {events.length === 0 ? (
          <p className="muted">Nog niets van belang.</p>
        ) : (
          events.map((e) => (
            <p key={e.ch}>
              <b>H{e.ch}</b> {e.text}
            </p>
          ))
        )}
      </div>
    </section>
  );
}

const STEPS = [
  ["Upload je e-book", "Een EPUB die je zelf hebt gekocht, of een boek uit het publieke domein."],
  [
    "FabulaWiki leest mee",
    "Per hoofdstuk worden personages, plaatsen, relaties en gebeurtenissen vastgelegd.",
  ],
  [
    "Zet je boekenlegger",
    "Je ziet alleen wat je tot en met dat hoofdstuk kunt weten. Ook namen die later pas onthuld worden.",
  ],
];

export function Welcome() {
  const { config, login } = useAuth();
  const [name, setName] = useState("");
  return (
    <div className="welcome">
      <div className="welcome-hero">
        <div className="welcome-intro">
          <span className="welcome-kicker">Spoilervrije wiki bij je eigen boek</span>
          <h1>Weet weer wie wie is — zonder spoilers.</h1>
          <p className="lead">
            Lang niet gelezen, of een boek met honderd personages? FabulaWiki maakt van je eigen boek
            een naslagwerk dat precies meegroeit met waar jij bent: personages, plaatsen en
            gebeurtenissen tot en met het hoofdstuk dat je hebt gelezen.
          </p>
          {config?.auth_mode === "dev" ? (
            <form
              className="row"
              onSubmit={(e) => {
                e.preventDefault();
                void login(name);
              }}
            >
              <label className="sr-only" htmlFor="devname">Naam</label>
              <input
                id="devname"
                placeholder="ontwikkelnaam, bv. admin"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <button className="btn" type="submit">Inloggen (dev)</button>
            </form>
          ) : (
            <button className="btn btn-lg" onClick={() => void login()}>
              Inloggen of account maken
            </button>
          )}
          <p className="welcome-note small">
            Je boeken blijven privé. Nieuwe accounts worden handmatig goedgekeurd.
          </p>
        </div>
        <Demo />
      </div>

      <ol className="welcome-steps">
        {STEPS.map(([title, text], i) => (
          <li key={title}>
            <span className="welcome-step-num">{i + 1}</span>
            <h3>{title}</h3>
            <p className="muted">{text}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}
